import {
  Injectable,
  TemplateRef,
  ComponentFactoryResolver,
  ComponentFactory,
  Injector,
  Inject
} from '@angular/core';
import { CELL_TEMPLATE_COMPONENT } from '../tokens';
import {
  FirestoreTableTemplateContext,
  FirestoreTableColumn,
  FirebaseTableTemplates,
  ICellTemplatesComponent
} from './table-definition';
import { NGXLogger } from 'ngx-logger';
import { DisplayService } from './display.service';
import { applyContextToFunctionOrConstant } from '@digitaix/types';

@Injectable({
  providedIn: 'root'
})
export class TemplateService<D> {
  protected _templates: FirebaseTableTemplates<D> = {};

  public get templates(): FirebaseTableTemplates<D> {
    return this._templates;
  }

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    private logger: NGXLogger,
    @Inject(CELL_TEMPLATE_COMPONENT) private cellTemplateComponent: any,
    private display: DisplayService<D, any>
  ) {
    if (this.cellTemplateComponent) {
      const factory: ComponentFactory<
        ICellTemplatesComponent<D>
      > = this.componentFactoryResolver.resolveComponentFactory<
        ICellTemplatesComponent<D>
      >(this.cellTemplateComponent);

      const componentRef = factory.create(this.injector);
      this._templates = {
        ...this._templates,
        empty: componentRef.instance.empty,
        boolean: componentRef.instance.boolean,
        object: componentRef.instance.object,
        plain: componentRef.instance.plain,
        array: componentRef.instance.array,
        relationLink: componentRef.instance.relationLink,
        color: componentRef.instance.color,
        emptyLoading: componentRef.instance.emptyLoading,
        button: componentRef.instance.button,
        relationLinkArray: componentRef.instance.relationLinkArray,
        resolveWithCollectionPath:
          componentRef.instance.resolveWithCollectionPath,
        actionButton: componentRef.instance.actionButton
      };
    } else {
      this.logger.warn(
        '[TemplateService::constructor] Injection Token CELL_TEMPLATE_COMPONENT is not defined',
        this.cellTemplateComponent
      );
    }
  }

  public addTemplate<V = any>(
    name: string,
    template: TemplateRef<FirestoreTableTemplateContext<D, V>>
  ) {
    this._templates[name] = template;
  }

  public removeTemplate(name: string): void {
    if (this._templates[name]) {
      delete this._templates[name];
    }
  }

  public getTemplate<V = any>(
    column: FirestoreTableColumn<D, V>,
    value: V,
    row: D
  ): TemplateRef<FirestoreTableTemplateContext<D, V>> {
    if (column.templateRef) {
      return column.templateRef;
    }

    if (this.templates.hasOwnProperty(column.templateName)) {
      if ((value === null || value === undefined) && column.allowEmpty) {
        return this._getDefaultTemplatee(column, value, row);
      }
      return this.templates[column.templateName];
    }

    if (column.action) {
      return this.templates.button;
    }

    if (column.collectionPath) {
      return this.templates.resolveWithCollectionPath;
    }

    return this._getDefaultTemplatee(column, value, row);
  }

  private _getDefaultTemplatee<V = any>(
    column: FirestoreTableColumn<D, V>,
    value: any,
    row: D
  ) {
    if (column.toDisplay) {
      value = applyContextToFunctionOrConstant(column.toDisplay, value, row);
    }
    const type = typeof value;

    switch (type) {
      case 'boolean':
        return this.templates.boolean;

      case 'object':
        if (!value) {
          if (column.async) {
            return this.templates.emptyLoading;
          } else {
            return this.templates.empty;
          }
        }
        if (Array.isArray(value)) {
          return this.templates.array;
        }
        if (value.hasOwnProperty('name')) {
          return this.templates.plain;
        }
        return this.templates.object;
    }

    return this.templates.plain;
  }
}
